import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User, School, Student } from '../lib/supabase';
import { useAuth } from './AuthContext';
// ─── Types ────────────────────────────────────────────────────

interface GhostSession {
  sessionId: string;
  user: User;
  school: School | null;
  linkedStudents: Student[];
}

interface GhostContextType {
  isGhostMode: boolean;
  ghostSession: GhostSession | null;
  startGhost: (targetUserId: string, adminId: string) => Promise<{ error: string | null }>;
  exitGhost: () => Promise<void>;
}

const GhostContext = createContext<GhostContextType | null>(null);

// ─── Provider ─────────────────────────────────────────────────

export function GhostProvider({ children }: { children: React.ReactNode }) {
  const [ghostSession, setGhostSession] = useState<GhostSession | null>(null);

  // A normal await-based supabase call can't be trusted to finish once the
  // browser has already decided to unload the page. We keep a plain ref to
  // the current session id + access token so the unload handler below can
  // fire a fetch(..., { keepalive: true }) synchronously — keepalive lets
  // the browser finish a small in-flight request even after the tab closes,
  // which a normal awaited call can't guarantee.
  const sessionIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    sessionIdRef.current = ghostSession?.sessionId || null;
  }, [ghostSession]);

  useEffect(() => {
    if (!ghostSession) return;
    supabase.auth.getSession().then(({ data }) => {
      tokenRef.current = data.session?.access_token || null;
    });
  }, [ghostSession?.sessionId]);

  useEffect(() => {
    const handleUnload = () => {
      const sessionId = sessionIdRef.current;
      const token = tokenRef.current;
      if (!sessionId || !token) return;
      // Same effect as exitGhost's audit-log update, just fired in a way
      // that survives the tab actually closing.
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ghost_sessions?id=eq.${sessionId}`, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ended_at: new Date().toISOString() }),
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, []);

  const startGhost = useCallback(async (
    targetUserId: string,
    adminId: string
  ): Promise<{ error: string | null }> => {
    try {
      // Fetch target user profile
      const { data: targetUser, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', targetUserId)
        .maybeSingle();

      if (userError || !targetUser) {
        return { error: 'Could not load user profile.' };
      }

      // Fetch their school if they have one — kept as its own query
      // (matching AuthContext's proven pattern) rather than an embedded
      // select, since that embed isn't used/verified anywhere else in
      // the codebase and turned out to fail against the real schema.
      let school: School | null = null;
      if (targetUser.school_id) {
        const { data: schoolData } = await supabase
          .from('schools')
          .select('*')
          .eq('id', targetUser.school_id)
          .maybeSingle();
        school = schoolData || null;
      }

      // Fetch linked students if parent — this no longer waits on the
      // audit log write, which now fires in parallel below instead of
      // sequentially after everything else.
      const linkedStudentsPromise: Promise<Student[]> = targetUser.role === 'parent'
        ? Promise.resolve(
            supabase
              .from('parent_students')
              .select('student_id, students(*)')
              .eq('parent_id', targetUserId)
          ).then(({ data }) => (data || []).map((r: any) => r.students).filter(Boolean) as Student[])
        : Promise.resolve([]);

      const sessionId = crypto.randomUUID();

      // Log ghost session to audit table — fire-and-forget. The admin
      // doesn't need to wait on this write to start viewing as the target;
      // if it fails we still record the attempt via console.error rather
      // than blocking or silently losing the audit trail.
      supabase
        .from('ghost_sessions')
        .insert({
          id: sessionId,
          admin_id: adminId,
          target_user_id: targetUserId,
          target_user_name: targetUser.full_name,
          target_role: targetUser.role,
          target_school_id: targetUser.school_id || null,
          target_school_name: school?.name || null,
          started_at: new Date().toISOString(),
        })
        .then(({ error }) => {
          if (error) console.error('[Ghost] audit log write failed:', error);
        });

      const linkedStudents = await linkedStudentsPromise;

      setGhostSession({
        sessionId,
        user: targetUser as User,
        school,
        linkedStudents,
      });

      return { error: null };
    } catch (e) {
      return { error: 'Failed to start ghost session.' };
    }
  }, []);

  const exitGhost = useCallback(async () => {
    if (ghostSession?.sessionId) {
      // Mark session as ended in audit log
      await supabase
        .from('ghost_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', ghostSession.sessionId);
    }
    setGhostSession(null);
  }, [ghostSession]);

  return (
    <GhostContext.Provider value={{
      isGhostMode: !!ghostSession,
      ghostSession,
      startGhost,
      exitGhost,
    }}>
      {children}
    </GhostContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────

export function useGhost() {
  const ctx = useContext(GhostContext);
  if (!ctx) throw new Error('useGhost must be used within GhostProvider');
  return ctx;
}

// ─── Ghost-aware useAuth replacement ──────────────────────────
// Import this in pages that need to be ghost-aware.
// It returns ghost user/school when in ghost mode,
// otherwise falls back to the real auth context.



export function useEffectiveAuth() {
  const auth = useAuth();
  const ghost = useGhost();

  if (ghost.isGhostMode && ghost.ghostSession) {
    return {
      ...auth,
      user: ghost.ghostSession.user,
      school: ghost.ghostSession.school,
      linkedStudents: ghost.ghostSession.linkedStudents,
      // Override signOut to exit ghost instead of signing out for real
      signOut: async () => {
        await ghost.exitGhost();
        window.location.href = '/admin';
      },
    };
  }

  return auth;
}
